/**
 * Minimal CSS-driven tooltip — hover or keyboard focus reveals a small
 * surface-raised bubble. Used for the §6.3 cross-field explanations on
 * disabled controls (disabled elements swallow JS events, but the parent's
 * CSS :hover still fires, which is exactly what this relies on).
 */

import type { ReactNode } from "react";

import { cx } from "../../lib/format";

export interface TooltipProps {
  /** Tooltip body — keep it to one plain-language sentence. */
  content: ReactNode;
  /** Render the wrapper as a block (full-width children like field groups). */
  block?: boolean;
  side?: "top" | "bottom";
  /** When true the bubble never shows (wrapper stays for stable layout). */
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

export function Tooltip({
  content,
  block = false,
  side = "top",
  disabled = false,
  className,
  children,
}: TooltipProps) {
  if (disabled) {
    return (
      <span className={cx("relative", block ? "block w-full" : "inline-flex", className)}>
        {children}
      </span>
    );
  }
  return (
    <span
      className={cx(
        "group/tooltip relative",
        block ? "block w-full" : "inline-flex",
        className,
      )}
      tabIndex={-1}
    >
      {children}
      <span
        role="tooltip"
        className={cx(
          "pointer-events-none absolute left-1/2 z-40 hidden w-max max-w-[260px] -translate-x-1/2",
          "whitespace-normal rounded border border-line bg-surface-raised px-2.5 py-1.5 text-left text-xs text-body",
          side === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5",
          "group-hover/tooltip:block group-focus-within/tooltip:block",
        )}
      >
        {content}
      </span>
    </span>
  );
}
